import React, { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ColumnDef, getExpandedRowModel } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import useEnterSearch from "hooks/useEnterSearch"
import withRouter from "Components/Common/withRouter"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TreeNameCell from "Components/atoms/tree-name-cell"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchSelect from "Components/molecule/inputs/search-select"
import { SelectOption } from "Components/molecule/inputs/select-input"
import { PencilIcon } from "Components/atoms/icons"
import DepartmentKPI from "models/department-kpi"
import Branch from "models/branch"
import FiscalYear from "models/fiscal-year"
import { getAllDepartmentKPIs, getDepartmentKPIFormFields } from "slices/admin/departments/thunk"
import { navigateToDepartmentDetails, navigateToEditDepartmentKPI } from "utils/navigator"
import { compareText, stripDepartmentPrefix } from "utils/department-options"
import { formatCurrency } from "utils/tools"
import FiscalCalendar from "utils/fiscal-calendar"

const MONEY_KEYS = [
    "budget", "annualRevenue", "grossMargin", "workInHand", "blueZone", "yellowZone",
    "opportunityWinTotal", "projectProfitability",
] as const
type MoneyKey = typeof MONEY_KEYS[number]

// One row of the hierarchy: Branch > Division > Department. Branch and division rows add up
// the KPIs under them; department rows are the KPI itself.
type KPINode = {
    id: string
    name: string
    kpi?: DepartmentKPI
    totals: Record<MoneyKey, number>
    subRows?: KPINode[]
}

type Filters = { fiscalYear: string, branchIds: string[], divisionIds: string[], departmentIds: string[] }

const emptyTotals = (): Record<MoneyKey, number> =>
    Object.fromEntries(MONEY_KEYS.map(k => [k, 0])) as Record<MoneyKey, number>

const addTotals = (into: Record<MoneyKey, number>, from: Record<MoneyKey, number>) =>
    MONEY_KEYS.forEach(k => { into[k] += from[k] })

const money = (header: string, key: MoneyKey, size = 120): ColumnDef<KPINode> => ({
    header,
    id: key,
    size,
    accessorFn: (n) => n.totals[key],
    meta: { getHeaderClassName: () => 'text-end', cellClassName: 'text-end' },
    cell: ({ getValue }) => formatCurrency(getValue() as number),
})

const defaultFilters = (departmentId: string | null): Filters => ({
    // Opened from a department's page: show every year of that department
    fiscalYear: departmentId ? '' : String(FiscalCalendar.lastMonthFiscalYear()),
    branchIds: [],
    divisionIds: [],
    departmentIds: departmentId ? [departmentId] : [],
})

// Department KPIs of every department the user can read, grouped Branch > Division > Department
// and filtered by fiscal year, branch, division and department. Each department's page still
// shows its own KPIs.
const DepartmentKPIsPage = (props: any) => {
    const [searchParams] = useSearchParams()
    const [kpis, setKPIs] = useState<DepartmentKPI[]>([])
    const [branches, setBranches] = useState<Branch[]>([])
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // Draft filters change with the inputs; applied filters change on Search / Enter
    const [filters, setFilters] = useState<Filters>(() => defaultFilters(searchParams.get("departmentId")))
    const [applied, setApplied] = useState<Filters>(filters)
    const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters(f => ({ ...f, [key]: value }))

    const handleSearch = () => setApplied(filters)
    const handleClearSearch = () => {
        const f = defaultFilters(null)
        setFilters(f)
        setApplied(f)
    }
    const { selectProps } = useEnterSearch(handleSearch)

    useEffect(() => {
        const init = async () => {
            const [rows, fields] = await Promise.all([getAllDepartmentKPIs(), getDepartmentKPIFormFields()])
            if (rows) setKPIs(rows)
            if (fields) {
                setBranches(fields.branches)
                setFiscalYears(fields.fiscalYears)
            }
            setIsLoading(false)
        }
        init()
    }, [])

    const divisionOptions = useMemo<SelectOption[]>(() => {
        const map = new Map<string, string>()
        kpis.forEach(k => {
            const p = k.department?.parentDepartment
            if (p) map.set(p.id, stripDepartmentPrefix(p.description))
        })
        return Array.from(map.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => compareText(a.label, b.label))
    }, [kpis])

    const departmentOptions = useMemo<SelectOption[]>(() => {
        const map = new Map<string, string>()
        kpis
            .filter(k => filters.divisionIds.length === 0 || filters.divisionIds.includes(k.department?.parentDepartment?.id ?? ''))
            .forEach(k => k.department && map.set(k.department.id, stripDepartmentPrefix(k.department.description)))
        return Array.from(map.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => compareText(a.label, b.label))
    }, [kpis, filters.divisionIds])

    const branchOptions = useMemo<SelectOption[]>(() =>
        branches.map(b => ({ value: b.id, label: b.name })).sort((a, b) => compareText(a.label, b.label)), [branches])

    const fiscalYearOptions = useMemo<SelectOption[]>(() => {
        const years = new Set<number>(fiscalYears.map(f => f.id))
        kpis.forEach(k => years.add(k.fiscalYear))
        return [{ value: '', label: 'All' }, ...Array.from(years).sort((a, b) => b - a).map(y => ({ value: String(y), label: String(y) }))]
    }, [fiscalYears, kpis])

    const tree = useMemo<KPINode[]>(() => {
        const rows = kpis.filter(k =>
            (applied.divisionIds.length === 0 || applied.divisionIds.includes(k.department?.parentDepartment?.id ?? ''))
            && (applied.departmentIds.length === 0 || applied.departmentIds.includes(k.departmentId))
            && (applied.branchIds.length === 0 || applied.branchIds.includes(k.branchId))
            && (!applied.fiscalYear || k.fiscalYear === Number(applied.fiscalYear))
        )

        const branchMap = new Map<string, KPINode & { divisions: Map<string, KPINode> }>()
        rows.forEach(k => {
            const branchId = k.branchId ?? 'none'
            let branch = branchMap.get(branchId)
            if (!branch) {
                branch = { id: `b-${branchId}`, name: k.branch?.name ?? '—', totals: emptyTotals(), subRows: [], divisions: new Map() }
                branchMap.set(branchId, branch)
            }

            const parent = k.department?.parentDepartment
            const divisionId = parent?.id ?? 'none'
            let division = branch.divisions.get(divisionId)
            if (!division) {
                division = {
                    id: `${branch.id}-d-${divisionId}`,
                    name: parent ? stripDepartmentPrefix(parent.description) : '—',
                    totals: emptyTotals(),
                    subRows: [],
                }
                branch.divisions.set(divisionId, division)
                branch.subRows!.push(division)
            }

            const totals = Object.fromEntries(MONEY_KEYS.map(key => [key, Number(k[key] ?? 0)])) as Record<MoneyKey, number>
            division.subRows!.push({
                id: `${division.id}-${k.departmentId}-${k.fiscalYear}`,
                name: stripDepartmentPrefix(k.department?.description),
                kpi: k,
                totals,
            })
            addTotals(division.totals, totals)
            addTotals(branch.totals, totals)
        })

        return Array.from(branchMap.values()).map(({ divisions, ...branch }) => branch)
    }, [kpis, applied])

    const columns = useMemo<ColumnDef<KPINode>[]>(() => [
        {
            header: "Name",
            id: "name",
            size: 300,
            accessorFn: (n) => n.name,
            cell: ({ row }) => {
                const kpi = row.original.kpi
                return <TreeNameCell row={row}>
                    {kpi
                        ? <a className="table-link" role="button" onClick={(e) => { e.stopPropagation(); navigateToDepartmentDetails(props.router.navigate, kpi.departmentId) }}>
                            {row.original.name}
                        </a>
                        : row.original.name}
                </TreeNameCell>
            },
        },
        { header: "Fiscal Year", id: "fiscalYear", size: 100, accessorFn: (n) => n.kpi?.fiscalYear, cell: ({ row }) => row.original.kpi?.fiscalYear ?? "" },
        money("Budget", "budget"),
        money("Annual Revenue", "annualRevenue", 130),
        money("Gross Margin", "grossMargin"),
        money("Work In Hand", "workInHand"),
        money("Blue Zone", "blueZone", 110),
        money("Yellow Zone", "yellowZone", 110),
        money("Opportunity Win Total", "opportunityWinTotal", 150),
        money("Project Profitability", "projectProfitability", 140),
        {
            header: "Performance Management",
            id: "performanceManagement",
            size: 200,
            accessorFn: (n) => n.kpi?.performanceManagement,
            cell: ({ row }) => row.original.kpi ? (row.original.kpi.performanceManagement ?? "—") : "",
        },
        {
            header: "",
            id: "actions",
            size: 60,
            enableSorting: false,
            enableHiding: false,
            cell: ({ row }) => {
                const kpi = row.original.kpi
                return kpi && <IconButton title="Edit" onClick={() => navigateToEditDepartmentKPI(props.router.navigate, kpi.departmentId, kpi.branchId, kpi.fiscalYear)}>
                    <PencilIcon />
                </IconButton>
            },
        },
    ], [])

    const table = useDataTable({
        data: tree,
        columns,
        getSubRows: (row) => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { expanded: true },
        initialSorting: [{ id: 'name', desc: false }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true,
    })

    const values = (options: SelectOption[], ids: string[]) => options.filter(o => ids.includes(o.value))

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Department KPIs"
        table={table}
        columns={columns}
        isLoading={isLoading}
        loadingTitle="Loading Department KPIs"
        rowCount={table.getCoreRowModel().flatRows.filter(r => !!r.original.kpi).length}
        filters={<SearchFormContainer
            inputs={<>
                <SearchSelect label="Fiscal Year" options={fiscalYearOptions} value={filters.fiscalYear} onChange={(o) => setFilter('fiscalYear', o?.value ?? '')} {...selectProps} />
                <MultiSelect label="Branch" placeholder="All" options={branchOptions} value={values(branchOptions, filters.branchIds)} onChange={(v) => setFilter('branchIds', v.map(o => o.value))} {...selectProps} />
                <MultiSelect label="Division" placeholder="All" options={divisionOptions} value={values(divisionOptions, filters.divisionIds)} onChange={(v) => setFilter('divisionIds', v.map(o => o.value))} {...selectProps} />
                <MultiSelect label="Department" placeholder="All" options={departmentOptions} value={values(departmentOptions, filters.departmentIds)} onChange={(v) => setFilter('departmentIds', v.map(o => o.value))} {...selectProps} />
            </>}
            clearSearchButton={<>
                <PrimaryButton onClick={handleSearch}>Search</PrimaryButton>
                <SecondaryButton onClick={handleClearSearch}>Clear Search</SecondaryButton>
            </>}
        />}
    />
}

export default withRouter(DepartmentKPIsPage)
